import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Airport } from '@flugwettrennen/shared';
import { api } from '../api';

interface Props {
  label: string;
  value: string;
  onChange: (iata: string) => void;
  disabled?: boolean;
  testId?: string;
}

export function AirportPicker({ label, value, onChange, disabled, testId }: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Airport[]>([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    const handle = setTimeout(() => {
      api.airports
        .search(query)
        .then((r) => setResults(r.airports))
        .catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="field" ref={boxRef} style={{ position: 'relative' }}>
      <label>{label}</label>
      <input
        type="text"
        value={open ? query : value}
        placeholder={t('create.searchAirport')}
        disabled={disabled}
        data-testid={testId}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
      />
      {open && results.length > 0 && (
        <div
          className="card"
          style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, padding: 6, maxHeight: 280, overflow: 'auto' }}
        >
          {results.map((a) => (
            <button
              key={a.iata}
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ width: '100%', justifyContent: 'flex-start' }}
              data-testid={`airport-option-${a.iata}`}
              onClick={() => {
                onChange(a.iata);
                setQuery('');
                setOpen(false);
              }}
            >
              <span className="mono" style={{ color: 'var(--amber-deep)', minWidth: 34 }}>{a.iata}</span>
              <span>{a.name}</span>
              <span className="muted" style={{ marginLeft: 'auto' }}>{a.countryName}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
